import { useApolloClient } from '@apollo/client'
import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import {
    ProductListingDetailInput,
    ProductListingDetailPayload,
} from '@deltek/specification-client/core/Models/productListingDetail'
import { Checkbox } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { SaveProductListingDetail } from 'bpm/api/graphQL/mutations/productListing.mutation'
import { ProductListingDetailsQuery } from 'bpm/api/graphQL/queries/product.queries'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { TVData } from '../../ProductCardManagementPage.types'
import { listingDetailToSaveInput } from './listingDetailUtils'

const useStyles = makeStyles({
    checkbox: {
        padding: 0,
        margin: -2,
    },
})

interface EditableCheckboxCellProps {
    value: boolean
    row: TVData
}

// Per-listing checked override — module level to survive virtualized remounts
const checkedOverrideCache: Record<string, boolean> = {}

/** Clears all cache overrides. Exposed for tests to reset module state between cases. */
export const clearCheckedOverrideCache = () => {
    Object.keys(checkedOverrideCache).forEach((key) => delete checkedOverrideCache[key])
}

/** Inline checkbox that saves `canAutoGenerateProductSpecification` on change via a fetch-then-save cycle.
 * Used for column: canAutoGenerateProductSpecification. */
const EditableCheckboxCell = ({ value, row }: EditableCheckboxCellProps) => {
    const classes = useStyles()
    const listingId = useMemo(() => row.original?.listingId, [row])
    const { showGlobalSnackbar } = useGlobalSnackbar()
    const client = useApolloClient()

    const [localChecked, setLocalChecked] = useState<boolean>(
        listingId !== undefined && listingId in checkedOverrideCache
            ? checkedOverrideCache[listingId]
            : (value ?? false)
    )
    const [isSaving, setIsSaving] = useState(false)
    // Ref-based guard prevents double-click without stale closure issues
    const isSavingRef = useRef(false)

    // Sync from prop when there is no user-set override. Once the parent refetches
    // and the prop converges with the cached value, clear the override so future
    // server-side changes can propagate again.
    useEffect(() => {
        if (listingId !== undefined && listingId in checkedOverrideCache) {
            if (value === checkedOverrideCache[listingId]) {
                delete checkedOverrideCache[listingId]
            }
            return
        }
        setLocalChecked(value ?? false)
    }, [value, listingId])

    const handleChange = useCallback(
        async (
            _event: React.ChangeEvent<HTMLInputElement>,
            checked: boolean
        ) => {
            if (isSavingRef.current) return
            isSavingRef.current = true
            if (listingId) checkedOverrideCache[listingId] = checked
            setLocalChecked(checked)
            setIsSaving(true)

            try {
                const fetchResult = await client.query<{
                    getListingDetail: Partial<ListingDetail>
                }>({
                    query: ProductListingDetailsQuery,
                    variables: {
                        listingIdInput: {
                            listingId,
                            logEventSource: 'BpmListingDetail',
                        },
                    },
                    fetchPolicy: 'network-only',
                })

                const detail = fetchResult?.data?.getListingDetail
                if (!detail) throw new Error('getListingDetail returned empty')

                await client.mutate<
                    { saveProductListingDetail: Partial<ProductListingDetailPayload> },
                    {
                        saveProductListingDetailInput: Partial<
                            ProductListingDetailInput
                        >
                    }
                >({
                    mutation: SaveProductListingDetail,
                    variables: {
                        saveProductListingDetailInput: listingDetailToSaveInput(
                            detail,
                            { canAutoGenerateProductSpecification: checked }
                        ),
                    },
                })

                isSavingRef.current = false
                setIsSaving(false)
            } catch (error: unknown) {
                isSavingRef.current = false
                if (listingId) delete checkedOverrideCache[listingId]
                setLocalChecked(!checked)
                setIsSaving(false)
                showGlobalSnackbar({
                    variables: {
                        message:
                            error instanceof Error
                                ? error.message
                                : 'Failed to update Auto Generate setting.',
                        variant: 'error',
                        id: `EditableCheckboxCell_Error_${listingId}`,
                    },
                })
            }
        },
        [listingId, client, showGlobalSnackbar]
    )

    if (!listingId) return <></>

    return (
        <Checkbox
            className={classes.checkbox}
            color="primary"
            checked={localChecked}
            indeterminate={isSaving}
            onChange={handleChange}
            disabled={isSaving}
            inputProps={{ 'aria-label': 'Auto Generate' }}
        />
    )
}

export default EditableCheckboxCell
